/**
 * Cliente HTTP de la base de ventas: wrappers tipados sobre `req()` de campana-nueva, como Público. Nunca se manda el
 * comercio (sale del token). Todo es del equipo: con el token de una persona de un cliente, el backend dice 403.
 */

import { descargarBinario, req, reqSubida } from "../campana-nueva/api";
import type { Resp } from "../wa-oficial/api";
import type * as T from "./types";

const B = "/activa/base-ventas";
const e = (id: string) => encodeURIComponent(id);

export const baseVentasApi = {
  /** La base en uso, qué tan vieja está y la carga sin terminar. */
  estado: (): Promise<Resp<T.EstadoBase>> => req("GET", B),
  /** El historial de cargas: las versiones que se usaron, de la más nueva a la más vieja. */
  historial: (): Promise<Resp<T.Version[]>> => req("GET", `${B}/versiones`),
  /** El CSV ya leído de una versión: «pedidos» (las ventas) o «clientes». */
  descargar: (v: Pick<T.Version, "id" | "numero">, archivo: "pedidos" | "clientes"): Promise<void> =>
    descargarBinario(`${B}/versiones/${e(v.id)}/descargar?archivo=${archivo}`, `base-${archivo}-v${v.numero}.csv`),
  /** «El comercio me confirmó que sus clientes aceptaron», sobre la base en uso: quién y cómo, y su respaldo (29/10). */
  declararConsentimiento: (id: string, como: string, respaldo: T.RespaldoParaSubir): Promise<Resp<T.Version>> =>
    req("POST", `${B}/versiones/${e(id)}/consentimiento`, { como, respaldo }),
  /** «¿Sigue valiendo la declaración de la versión anterior?» → «Sí»: se copia con quien lo confirma y la fecha de hoy. */
  heredarConsentimiento: (id: string): Promise<Resp<T.Version>> => req("POST", `${B}/versiones/${e(id)}/consentimiento`, { heredar: true }),
  /** Antes de declarar o de heredar: la declaración anterior, a cuántos les vuelve «sí» y lo ya preparado. */
  panoramaConsentimiento: (id: string): Promise<Resp<T.PanoramaConsentimiento>> => req("GET", `${B}/versiones/${e(id)}/consentimiento`),
  /** El respaldo de la declaración de una versión (la captura o el PDF), tal cual se subió. */
  descargarRespaldo: (v: Pick<T.Version, "id">, nombre: string): Promise<void> => descargarBinario(`${B}/versiones/${e(v.id)}/consentimiento/respaldo`, nombre),
  retirarConsentimiento: (id: string): Promise<Resp<T.Version>> => req("DELETE", `${B}/versiones/${e(id)}/consentimiento`),

  /** Un borrador nuevo, para subir los dos archivos. */
  crearCarga: (): Promise<Resp<T.Carga>> => req("POST", `${B}/cargas`),
  verCarga: (id: string): Promise<Resp<T.Carga>> => req("GET", `${B}/cargas/${e(id)}`),
  /** Una parte de un archivo, tal cual: la 0 lo empieza de nuevo y las demás tienen que ser la que falta. */
  subirParte: (id: string, archivo: T.Archivo, p: { parte: number; bytes: number; nombre: string }, cuerpo: Blob, senal?: AbortSignal): Promise<Resp<T.Carga>> =>
    reqSubida(`${B}/cargas/${e(id)}/archivos/${archivo}?parte=${p.parte}&bytes=${p.bytes}&nombre=${encodeURIComponent(p.nombre)}`, cuerpo, senal),
  /** Lee los dos archivos en segundo plano (202): la pantalla consulta la carga hasta que queda para revisar. */
  leer: (id: string): Promise<Resp<T.Carga>> => req("POST", `${B}/cargas/${e(id)}/leer`),
  /** Vuelve a revisar con las columnas que eligió la persona. */
  revisar: (id: string, mapeo: T.Mapeo): Promise<Resp<T.Carga>> => req("POST", `${B}/cargas/${e(id)}/revisar`, { mapeo }),
  /** Guarda lo revisado (con la huella de esa revisión) en segundo plano (202). */
  confirmar: (id: string, huella: string): Promise<Resp<T.Carga>> => req("POST", `${B}/cargas/${e(id)}/confirmar`, { huella }),
  descartar: (id: string): Promise<Resp<{ descartada: true }>> => req("DELETE", `${B}/cargas/${e(id)}`),
};
